import { useEffect, useMemo, useState } from "react";
import { getInvoicesNotPaid } from "@/api/invoices";
import { checkEmployeeHierarchy } from "@/api/utils";
import useDashboardFunc from "@/hooks/useDashboard";
import { AssignmentProps } from "@/interfaces/assignment_types";
import { BaseDashboardLayoutProps } from "@/interfaces/dashboard_types";
import { RoleHierarchy } from "@/interfaces/employee_type";
import { InvoiceProps } from "@/interfaces/invoices_types";
import { ProjectProps } from "@/interfaces/project_types";
import { RFIProps } from "@/interfaces/rfi_types";
import { SubmittalProps } from "@/interfaces/submittal_types";
import { TaskProps } from "@/interfaces/tasks_types";
import { AssignedDashboardContainer } from "./AssignedDashboard";
/**
 * A small component for the Main Menu component
 *
 * Shows the user's name and role, along with tasks and projects
 *
 * Dashboard will show different panels depending on the user's role
 *
 * @param user The user to display the dashboard for
 * @param announcements The announcements to display
 * @param contactUs The contact us to display
 */

export function BaseDashboardLayout({
	user,
	announcements,
	contactUs,
}: BaseDashboardLayoutProps) {
	const { isNewCard, markAsSeen, pruneSeenCards } = useDashboardFunc();
	const [invoices, setInvoices] = useState<InvoiceProps[]>([]);

	// The only sorting here only involes "isNewCard"
	// Since that is the only data that the backend doesn't have
	// (isNewCard is stored on the browser's localStorage)
	// Everything else is sorted on the backend

	const sort_tasks = useMemo(() => {
		return (tasks: TaskProps[]) => {
			return [...tasks].sort((a, b) => {
				const aIsNew = isNewCard("task", a.task_id ?? "");
				const bIsNew = isNewCard("task", b.task_id ?? "");
				if (aIsNew && !bIsNew) return -1;
				if (!aIsNew && bIsNew) return 1;
				return 0;
			});
		};
	}, [isNewCard]);

	const sort_projects = useMemo(() => {
		return (projects: ProjectProps[]) => {
			return [...projects].sort((a, b) => {
				const aIsNew = isNewCard("project", a.project_id ?? "");
				const bIsNew = isNewCard("project", b.project_id ?? "");
				if (aIsNew && !bIsNew) return -1;
				if (!aIsNew && bIsNew) return 1;
				return 0;
			});
		};
	}, [isNewCard]);

	const sort_submittals = useMemo(() => {
		return (submittals: SubmittalProps[]) => {
			return [...submittals].sort((a, b) => {
				const aIsNew = isNewCard("submittal", a.submittal_id ?? "");
				const bIsNew = isNewCard("submittal", b.submittal_id ?? "");
				if (aIsNew && !bIsNew) return -1;
				if (!aIsNew && bIsNew) return 1;
				return 0;
			});
		};
	}, [isNewCard]);

	const sort_rfis = useMemo(() => {
		return (rfis: RFIProps[]) => {
			return [...rfis].sort((a, b) => {
				const aIsNew = isNewCard("rfi", a.RFI_id ?? "");
				const bIsNew = isNewCard("rfi", b.RFI_id ?? "");
				if (aIsNew && !bIsNew) return -1;
				if (!aIsNew && bIsNew) return 1;
				return 0;
			});
		};
	}, [isNewCard]);

	const sort_assignments = useMemo(() => {
		return (assignments: AssignmentProps[]) => {
			return [...assignments].sort((a, b) => {
				const aIsNew = isNewCard("assignment", a.id ?? "");
				const bIsNew = isNewCard("assignment", b.id ?? "");
				if (aIsNew && !bIsNew) return -1;
				if (!aIsNew && bIsNew) return 1;
				return 0;
			});
		};
	}, [isNewCard]);

	const sort_invoices = useMemo(() => {
		return (invoices: InvoiceProps[]) => {
			return [...invoices].sort((a, b) => {
				const aIsNew = isNewCard("invoice", a.invoice_id ?? "");
				const bIsNew = isNewCard("invoice", b.invoice_id ?? "");
				if (aIsNew && !bIsNew) return -1;
				if (!aIsNew && bIsNew) return 1;
				return 0;
			});
		};
	}, [isNewCard]);

	// Remove seenCards entries for cards no longer returned by the backend
	// (the backend only sends "Active" items, so anything missing is stale)
	useEffect(() => {
		const validKeys = new Set<string>();
		user.tasks?.forEach((t) => validKeys.add(`task-${t.task_id}`));
		user.projects?.forEach((p) => validKeys.add(`project-${p.project_id}`));
		user.submittals?.forEach((s) =>
			validKeys.add(`submittal-${s.submittal_id}`),
		);
		user.RFIs?.forEach((r) => validKeys.add(`rfi-${r.RFI_id}`));
		user.assignments?.forEach((a) => validKeys.add(`assignment-${a.id}`));
		pruneSeenCards(validKeys, [
			"task",
			"project",
			"submittal",
			"rfi",
			"assignment",
		]);
	}, [user, pruneSeenCards]);

	// Get the invoices that are not paid (ONLY ACTIVATED FOR ACCOUNTANTS)
	useEffect(() => {
		const get_invoices = async () => {
			try {
				const response = await getInvoicesNotPaid();
				setInvoices(response.data);
				pruneSeenCards(
					new Set(
						response.data.map((invoice) => `invoice-${invoice.invoice_id}`),
					),
					["invoice"],
				);
			} catch (error) {
				console.warn("Error getting invoices", error);
			}
		};

		const roleHierarchy = checkEmployeeHierarchy(user.role);

		if (roleHierarchy === "FINANCE" || roleHierarchy === "ADMIN") {
			get_invoices();
		}
	}, []);

	const sorted_tasks = useMemo(
		() => sort_tasks(user.tasks ?? []),
		[user.tasks, sort_tasks],
	);
	const sorted_projects = useMemo(
		() => sort_projects(user.projects ?? []),
		[user.projects, sort_projects],
	);
	const sorted_submittals = useMemo(
		() =>
			sort_submittals(
				user.submittals?.filter((submittal) => submittal.status !== "Closed") ??
					[],
			),
		[user.submittals, sort_submittals],
	);
	const sorted_rfis = useMemo(
		() => sort_rfis(user.RFIs?.filter((rfi) => rfi.status !== "Closed") ?? []),
		[user.RFIs, sort_rfis],
	);
	const sorted_invoices = useMemo(
		() => sort_invoices(invoices),
		[invoices, sort_invoices],
	);
	const sorted_assignments = useMemo(
		() => sort_assignments(user.assignments ?? []),
		[user.assignments, sort_assignments],
	);

	const roleHierarchy = checkEmployeeHierarchy(
		user.role,
	) as keyof typeof RoleHierarchy;

	return (
		<div className="flex flex-col w-full dark:bg-stone-800">
			<AssignedDashboardContainer
				announcements={announcements}
				sorted_tasks={sorted_tasks}
				sorted_projects={sorted_projects}
				sorted_submittals={sorted_submittals}
				sorted_rfis={sorted_rfis}
				sorted_assignments={sorted_assignments}
				isNewCard={isNewCard}
				markAsSeen={markAsSeen}
				contactUs={contactUs}
				role={roleHierarchy}
				sorted_invoices={sorted_invoices}
			/>
		</div>
	);
}
